/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { concatInitDataIdAndCertificate, extractContentId } from "./utils";

declare global {
  interface WebKitMediaKeys {
    createSession(mimeType: string, initData: Uint8Array): MediaKeySession;
  }

  interface WebKitMediaKeysConstructor {
    new (keySystem: string): WebKitMediaKeys;
    prototype: WebKitMediaKeys;
  }

  let WebKitMediaKeys: WebKitMediaKeysConstructor;

  interface WebKitMediaKeyNeededEvent extends Event {
    target: HTMLMediaElement;
    initData: Uint8Array;
  }

  interface WebKitLicenceRequestReadyEvent {
    target: MediaKeySession;
    message: Uint8Array;
  }

  interface HTMLMediaElement {
    webkitKeys?: WebKitMediaKeys;
    webkitSetMediaKeys(mediaKeys: WebKitMediaKeys): void;
  }

  interface MediaKeySession {
    contentId: string;
  }

  interface MediaKeySessionEventMap {
    webkitkeymessage: WebKitLicenceRequestReadyEvent;
    webkitkeyadded: Event;
    webkitkeyerror: Event;
  }

  interface ElementEventMap {
    webkitneedkey: WebKitMediaKeyNeededEvent;
  }
}

export class FairPlayLoader {
  private certificate = new Uint8Array();
  private licenseUrl: string = "";
  private certificateUri: string = "";

  /**
   * Initializes the required configurations with the provided certificate and license URL.
   */
  initialize = (certificateUri: string, licenseUrl: string) => {
    this.certificateUri = certificateUri;
    this.licenseUrl = licenseUrl;
  };

  /**
   * Loads a DRM certificate from the specified URI and stores it as a Uint8Array.
   *
   */
  async loadCertificate() {
    const response = await fetch(this.certificateUri);

    if (!response.ok) {
      console.error("Unable to load DRM certificate");
    }

    const arrayBuffer = await response.arrayBuffer();
    this.certificate = new Uint8Array(arrayBuffer);

    return this.certificate;
  }

  /**
   * Adds event listeners to a given video element to handle DRM-related events and video errors.
   *
   * This function is used to attach specific event handlers to a video element to support
   * playback with digital rights management (DRM).
   *
   */
  injectLoader = (videoElement: HTMLMediaElement) => {
    videoElement.addEventListener("webkitneedkey", this.onNeedKey);
    videoElement.addEventListener("error", this.onVideoError);
  };

  /**
   * Handles the `needkey` event triggered by a video element to initialize
   * and configure encrypted media playback using WebKit MediaKeys.
   *
   * This function is invoked when the video element requires decryption keys
   * for encrypted media streams. It extracts the content ID from the initialization
   * data and prepares the necessary key session to enable playback.
   *
   */
  private onNeedKey = (event: WebKitMediaKeyNeededEvent) => {
    const video = event.target;
    const contentId = extractContentId(event.initData);
    const initData = concatInitDataIdAndCertificate(
      event.initData,
      contentId,
      this.certificate,
    );

    if (!video.webkitKeys) {
      video.webkitSetMediaKeys(new WebKitMediaKeys("com.apple.fps.1_0"));
    }

    if (!video.webkitKeys) {
      throw "Could not create MediaKeys";
    }

    const keySession = video.webkitKeys.createSession("video/mp4", initData);

    if (!keySession) {
      throw "Could not create key session";
    }

    keySession.contentId = contentId;

    keySession.addEventListener("webkitkeymessage", this.licenseRequestReady);
    keySession.addEventListener("webkitkeyadded", this.onKeyAdded);
    keySession.addEventListener("webkitkeyerror", this.onKeyError);
  };

  /**
   * Handles the license request event when a `WebKitLicenceRequestReadyEvent` is triggered.
   *
   * This function processes the license request by sending the `event.message` to a license server
   * specified by `licenseUrl`. It uses a POST request with the appropriate headers set for
   * sending the message as binary data. Upon receiving a successful response, it updates the
   * session with the license obtained from the server.
   *
   */
  private licenseRequestReady = async (
    event: WebKitLicenceRequestReadyEvent,
  ) => {
    const session = event.target;
    const message = event.message;

    const response = await fetch(this.licenseUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
      },
      body: message,
    });

    if (!response.ok) {
      console.error("License request failed");
    }

    const arrayBuffer = await response.arrayBuffer();
    await session.update(new Uint8Array(arrayBuffer));
  };

  private onKeyAdded = () => {
    console.log("Decryption key was added to session.");
  };

  private onKeyError = () => {
    console.error("A decryption key error was encountered");
  };

  private onVideoError = () => {
    console.error("A video playback error occurred");
  };
}
